/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import { UserParameters } from "features/userParameters/UserParametersSchema"
import * as XLSX from "xlsx"

const formatMonthYear = (date: { month: number; year: number } | undefined): string => {
  if (!date) return ""
  return `${date.month}/${date.year}`
}

export const buildUserParametersWorkbook = (parameters: UserParameters): XLSX.WorkBook => {
  const workbook = XLSX.utils.book_new()

  // Create Meta Data sheet
  const metaData = [
    ["API version", 1],
    ["Creation date", new Date()],
  ]
  const metaDataSheet = XLSX.utils.aoa_to_sheet(metaData)
  XLSX.utils.book_append_sheet(workbook, metaDataSheet, "Meta Data")

  // Create General Parameters sheet
  const generalParameters = [
    ["Initial Portfolio Total", parameters.initialPortfolioDollars],
    ["Retirement Start", formatMonthYear(parameters.retirementStart)],
    ["Retirement Horizon", parameters.retirementHorizonMonths],
    ["Final Value Target", parameters.finalValuePctOfInitial],
    ["Initial Consumption Amount", parameters.initialConsumptionPerAnnumDollars ?? ""],
    ["Inflation Rate", parameters.inflationPerAnnumPct],
    ["CAPE Model", parameters.cape],
    ["Allocation Mode", parameters.allocationMode],
  ]

  // Add glide path length if applicable
  if (
    parameters.allocationMode === "glide path" &&
    parameters.glidePathLengthMonths !== undefined
  ) {
    generalParameters.push(["Glide Path Length", parameters.glidePathLengthMonths])
  }

  const generalParametersSheet = XLSX.utils.aoa_to_sheet(generalParameters)
  XLSX.utils.book_append_sheet(workbook, generalParametersSheet, "General Parameters")

  // Create Assets sheet
  const assetsHeader = [
    [null, "Allocation", null, null, null, null, null, null, null, "Fama French Factors", null],
    [
      "Name",
      "Expense Ratio",
      "Stocks: S&P 500",
      "Stocks: Non-US",
      "Cash: 3m U.S. T-bills",
      "Bonds: 10y U.S. Treasury",
      "Bonds: 30y U.S. Treasury",
      "Commodities",
      "Gold",
      "Small Stocks (SMB)",
      "Value Stocks (HML)",
    ],
  ]

  // Add initial assets row
  const initialAssetsRow = [
    "Initial",
    parameters.initialAssets.expenseRatioPerAnnumPct,
    parameters.initialAssets.allocation.spxTrPct,
    parameters.initialAssets.allocation.worldExUsEquitiesPct,
    parameters.initialAssets.allocation.cashPct,
    parameters.initialAssets.allocation.bm10yPct,
    parameters.initialAssets.allocation.bm30yPct,
    parameters.initialAssets.allocation.commoditiesPct,
    parameters.initialAssets.allocation.goldPct,
    parameters.initialAssets.famaFrenchFactors.smallStocksPct,
    parameters.initialAssets.famaFrenchFactors.valueStocksPct,
  ]

  const assetsRows = [initialAssetsRow]

  // Add final assets row if applicable
  if (parameters.allocationMode === "glide path" && parameters.finalAssets) {
    const finalAssetsRow = [
      "Final",
      parameters.finalAssets.expenseRatioPerAnnumPct,
      parameters.finalAssets.allocation.spxTrPct,
      parameters.finalAssets.allocation.worldExUsEquitiesPct,
      parameters.finalAssets.allocation.cashPct,
      parameters.finalAssets.allocation.bm10yPct,
      parameters.finalAssets.allocation.bm30yPct,
      parameters.finalAssets.allocation.commoditiesPct,
      parameters.finalAssets.allocation.goldPct,
      parameters.finalAssets.famaFrenchFactors.smallStocksPct,
      parameters.finalAssets.famaFrenchFactors.valueStocksPct,
    ]
    assetsRows.push(finalAssetsRow)
  }

  const assetData = [...assetsHeader, ...assetsRows]
  const assetsSheet = XLSX.utils.aoa_to_sheet(assetData)
  XLSX.utils.book_append_sheet(workbook, assetsSheet, "Assets")

  // Create Cash Flows sheet
  const cashFlowsHeader = [["Name", "Amount", "Frequency", "Inflation Adjusted", "Start", "End"]]

  const cashFlowRows = parameters.cashFlows.map(cf => [
    cf.name,
    cf.amount,
    cf.frequency,
    cf.inflationAdjusted ? "yes" : "no",
    formatMonthYear(cf.start),
    formatMonthYear(cf.end),
  ])

  const cashFlowData = [...cashFlowsHeader, ...cashFlowRows]
  const cashFlowsSheet = XLSX.utils.aoa_to_sheet(cashFlowData)
  XLSX.utils.book_append_sheet(workbook, cashFlowsSheet, "Cash Flows")

  return workbook
}

export const downloadWorkbook = (parameters: UserParameters): void => {
  const workbook = buildUserParametersWorkbook(parameters)
  const currentDate = new Date()

  // Format date and time to include year, month, day, hour, and minute
  const formattedDate = `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1)
    .toString()
    .padStart(2, "0")}-${currentDate.getDate().toString().padStart(2, "0")}`

  const formattedTime = `${currentDate.getHours().toString().padStart(2, "0")}-${currentDate
    .getMinutes()
    .toString()
    .padStart(2, "0")}`

  const filename = `srs-parameters-${formattedDate}_${formattedTime}.xlsx`

  // Use SheetJS's writeFile function which is designed to work in browsers
  // and should trigger the browser's save dialog
  XLSX.writeFile(workbook, filename, { bookType: "xlsx" })
}
